import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, User, Mail, Calendar, ExternalLink, Trash2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';

const RegisteredUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [deletedCount, setDeletedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const getAuthToken = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.tokens?.idToken || user?.idToken || '';
    } catch { return ''; }
  };

  useEffect(() => {
    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(
          `${API_BASE}/admin/registered-users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          if (res.status === 403) setError('Access denied. Admin only.');
          else if (res.status === 401) setError('Authentication required.');
          else setError(`Failed to load users (${res.status})`);
          return;
        }
        const data = await res.json();
        setUsers(data.users || []);
        setDeletedCount(data.deleted_accounts || 0);
      } catch (err) {
        console.error('Failed to fetch registered users:', err);
        setError('Failed to connect to server.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return dateStr; }
  };

  const getProfileLink = (user) => {
    if (user.profile_slug) return `/in/${user.profile_slug}`;
    if (user.public_link_id) return `/profile/public/${user.public_link_id}`;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading registered users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Error</div>
          <p className="text-red-700 mb-4">{error}</p>
          <button onClick={() => navigate('/platform-analytics')} className="btn btn-primary">
            Back to Analytics
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <button
            onClick={() => navigate('/platform-analytics')}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mb-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Platform Analytics
          </button>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="w-8 h-8 text-primary-600" />
            Registered User Accounts
          </h1>
          <p className="text-gray-600 mt-1">{users.length} active registered users</p>
        </div>

        {deletedCount > 0 && (
          <div className="bg-gray-100 rounded-lg px-5 py-3 text-center">
            <div className="flex items-center gap-2 text-gray-500">
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-medium">Deleted Accounts</span>
            </div>
            <p className="text-2xl font-bold text-gray-700 mt-1">{deletedCount}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((user) => {
          const profileLink = getProfileLink(user);
          return (
            <div
              key={user.user_id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 flex flex-col items-center text-center"
            >
              {user.photo_url ? (
                <img
                  src={user.photo_url}
                  alt={user.name || 'User'}
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 mb-3"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div
                className={`w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-3 ${user.photo_url ? 'hidden' : ''}`}
              >
                <User className="w-10 h-10 text-gray-400" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 truncate w-full">
                {user.name || 'Unnamed User'}
              </h3>
              {user.headline && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{user.headline}</p>
              )}

              <div className="mt-3 space-y-1 w-full text-sm text-gray-500">
                <div className="flex items-center justify-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="truncate">{user.email || 'No email'}</span>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Joined {formatDate(user.created_at)}</span>
                </div>
              </div>

              {profileLink ? (
                <Link
                  to={profileLink}
                  className="mt-4 btn btn-outline text-sm inline-flex items-center gap-1.5 w-full justify-center"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Profile
                </Link>
              ) : (
                <span className="mt-4 text-xs text-gray-400">No public profile</span>
              )}
            </div>
          );
        })}
      </div>

      {users.length === 0 && (
        <div className="text-center py-16 text-gray-500">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">No registered users found.</p>
        </div>
      )}
    </div>
  );
};

export default RegisteredUsersPage;
